import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldWarning, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const KNOWN_DEFAULTS = new Set([
  "admin123", "agent123", "admin", "password", "changeme", "12345678", "welcome1",
]);

/**
 * Blocking modal shown when the currently-signed-in user's account still has
 * a default seeded password. Cannot be dismissed — clicking outside does
 * nothing, no close button. Only submitting a strong new password unlocks the
 * app.
 *
 * Renders inside AppLayout so every authenticated route inherits it.
 */
export default function ForcePasswordChange() {
  const { user, refreshMe, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user || !user.must_change_password) return null;

  const strong =
    next.length >= 8 &&
    !KNOWN_DEFAULTS.has(next.toLowerCase()) &&
    next === confirm &&
    next !== current;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New password and confirm do not match."); return; }
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (KNOWN_DEFAULTS.has(next.toLowerCase())) {
      setError("That's one of the well-known defaults. Pick something else.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password rotated — you're all set");
      // Refresh /auth/me so must_change_password flips to false
      if (refreshMe) await refreshMe();
      else window.location.reload();
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      data-testid="force-password-change-modal"
    >
      <div className="bg-white border-2 border-primary w-full max-w-md">
        <div className="px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 border border-amber-400">
              <ShieldWarning size={22} weight="fill" className="text-amber-700" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">Password rotation required</div>
              <div className="font-display text-lg font-bold">Choose a new password</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            Your account is still using a default seeded password. For security, pick a new one before continuing.
            You&apos;ll be signed in on this device once the change is saved.
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Current password">
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              autoComplete="current-password"
              required
              data-testid="force-pw-current"
              className="w-full px-3 py-2 border border-border bg-transparent text-sm focus:border-primary focus:outline-none"
            />
          </Field>
          <Field label="New password (min 8 chars, no defaults)">
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="force-pw-new"
              className="w-full px-3 py-2 border border-border bg-transparent text-sm focus:border-primary focus:outline-none"
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="force-pw-confirm"
              className="w-full px-3 py-2 border border-border bg-transparent text-sm focus:border-primary focus:outline-none"
            />
          </Field>
          {error && (
            <div className="text-xs text-red-700 border border-red-300 bg-red-50 px-3 py-2" data-testid="force-pw-error">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={logout}
              className="text-xs text-muted-foreground hover:underline"
              data-testid="force-pw-logout"
            >
              Sign out instead
            </button>
            <button
              type="submit"
              disabled={!strong || busy}
              data-testid="force-pw-submit"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-[#002277] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Saving…" : "Set new password"}
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">{label}</div>
      {children}
    </div>
  );
}
